import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions, Tag, Button, Space, Spin, Typography, Modal, message, Table,
} from 'antd';
import { EditOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import {
  getReportDefinition, deleteReportDefinition,
  type ReportDefinition, type ReportParameter,
} from '@/api/dashboardApi';
import { useAuth } from '@/hooks/useAuth';

const { Title } = Typography;

export default function ReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [report, setReport] = useState<ReportDefinition | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canWrite = hasPermission('reports', 'write');
  const canDelete = hasPermission('reports', 'delete');

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getReportDefinition(id);
      if (res.data) {
        setReport(res.data);
      } else {
        setError(t('errors.report_definition_not_found'));
      }
    } catch {
      setError(t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = useCallback(() => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('reports.confirm_delete'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteReportDefinition(id);
          message.success(t('reports.delete_success'));
          navigate('/reports');
        } catch (err) {
          message.error(err instanceof Error ? err.message : t('app.error'));
        }
      },
    });
  }, [id, t, navigate]);

  if (loading) return <Spin size="large" />;
  if (error || !report) {
    return (
      <div>
        <Typography.Text type="danger">{error || t('errors.report_definition_not_found')}</Typography.Text>
        <br />
        <Button onClick={() => navigate('/reports')}>{t('app.back')}</Button>
      </div>
    );
  }

  const paramColumns: ColumnsType<ReportParameter> = [
    {
      title: t('reports.parameter.key'),
      dataIndex: 'key',
      key: 'key',
      width: 140,
    },
    {
      title: t('reports.parameter.label'),
      dataIndex: 'label',
      key: 'label',
      width: 160,
    },
    {
      title: t('reports.parameter.type'),
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (val: string) => <Tag>{val}</Tag>,
    },
    {
      title: t('reports.parameter.default_value'),
      dataIndex: 'default_value',
      key: 'default_value',
      render: (val: string) => val || '—',
    },
    {
      title: t('reports.parameter.required'),
      dataIndex: 'required',
      key: 'required',
      width: 80,
      align: 'center',
      render: (val: boolean) => val ? <Tag color="red">{t('app.yes')}</Tag> : t('app.no'),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports')}>
            {t('app.back')}
          </Button>
          <Title level={4} style={{ margin: 0 }}>{report.name}</Title>
        </Space>
        <Space>
          {canWrite && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/reports/${id}/edit`)}
            >
              {t('app.edit')}
            </Button>
          )}
          {canDelete && (
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('app.delete')}
            </Button>
          )}
        </Space>
      </div>

      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label={t('reports.field.name')}>{report.name}</Descriptions.Item>
        <Descriptions.Item label={t('reports.field.category')}>
          {report.category ? <Tag>{t(`reports.category.${report.category}`)}</Tag> : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('reports.field.description')} span={2}>
          {report.description || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('reports.field.output_format')}>
          <Tag color="blue">{report.output_format ? t(`reports.format.${report.output_format}`) : '—'}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('reports.field.owner')}>{report.owner_id}</Descriptions.Item>
        <Descriptions.Item label={t('reports.field.query_template')} span={2}>
          <pre style={{ margin: 0, fontSize: 12, maxHeight: 200, overflow: 'auto', background: '#fafafa', padding: 8 }}>
            {report.query_template || '—'}
          </pre>
        </Descriptions.Item>
      </Descriptions>

      {(report.parameters ?? []).length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Title level={5}>{t('reports.field.parameters')}</Title>
          <Table<ReportParameter>
            dataSource={report.parameters}
            columns={paramColumns}
            rowKey="key"
            pagination={false}
            size="small"
          />
        </div>
      )}

      <Descriptions bordered column={2} size="small" style={{ marginTop: 16 }}>
        <Descriptions.Item label={t('customers.field.created_by')}>
          {report.created_by}
        </Descriptions.Item>
        <Descriptions.Item label={t('customers.field.created_at')}>
          {new Date(report.created_at).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label={t('customers.field.updated_by')}>
          {report.updated_by}
        </Descriptions.Item>
        <Descriptions.Item label={t('customers.field.updated_at')}>
          {new Date(report.updated_at).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label={t('customers.field.branch_id')}>
          {report.branch_id}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
}
